import { ArrowDownTrayIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import React, { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { useDownloadFax } from '@/components/fax/hooks/useDownloadFax';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  FaxTransmission,
  useListFaxTransmissionsByMatterQuery,
  useRefreshFaxStatusMutation,
} from '@/services/api/faxService';

interface FaxTransmissionTableProps {
  matterId: string;
}

const getStatusBadgeVariant = (status: string) => {
  switch (status.toLowerCase()) {
    case 'delivered':
      return 'default';
    case 'sent':
    case 'sending':
      return 'secondary';
    case 'failed':
    case 'error':
      return 'destructive';
    case 'pending':
    case 'queued':
      return 'outline';
    default:
      return 'secondary';
  }
};

const formatPhoneNumber = (phoneNumber: string) => {
  const cleaned = phoneNumber.replace(/\D/g, '');
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  return phoneNumber;
};

const isDownloadAvailable = (transmission: FaxTransmission): boolean => {
  const hasFileUrl = !!transmission.file_url;
  const isValidStatus =
    transmission.status === 'sent' || transmission.status === 'delivered';
  return hasFileUrl && isValidStatus;
};

const getDownloadDisabledReason = (
  transmission: FaxTransmission,
): string | null => {
  if (!transmission.file_url) {
    if (transmission.status === 'sent' || transmission.status === 'delivered') {
      return 'File not available. Try refreshing';
    }
    return 'Download URL not available';
  }
  if (transmission.status !== 'sent' && transmission.status !== 'delivered') {
    return 'Download only available for sent/delivered faxes';
  }
  return null;
};

export const FaxTransmissionTable: React.FC<FaxTransmissionTableProps> = ({
  matterId,
}) => {
  const [refreshingId, setRefreshingId] = useState<string | null>(null);

  const {
    data: transmissionData,
    isLoading,
    error,
    refetch,
  } = useListFaxTransmissionsByMatterQuery(matterId);

  const [refreshFaxStatus] = useRefreshFaxStatusMutation();
  const { downloadFax, isDownloading } = useDownloadFax();

  const transmissions = useMemo(
    () => transmissionData?.transmissions || [],
    [transmissionData?.transmissions],
  );

  const handleRefreshStatus = useCallback(
    async (transmissionId: string) => {
      try {
        setRefreshingId(transmissionId);
        const result = await refreshFaxStatus(transmissionId).unwrap();
        toast.success(`Status updated: ${result.transmission.status}`);
      } catch (refreshError) {
        console.error('Failed to refresh fax status:', refreshError);
        toast.error('Failed to refresh status. Please try again.');
      } finally {
        setRefreshingId(null);
      }
    },
    [refreshFaxStatus],
  );

  const handleRefreshAll = useCallback(async () => {
    try {
      await refetch();
      toast.success('All transmissions refreshed');
    } catch (refreshError) {
      console.error('Failed to refresh transmissions:', refreshError);
      toast.error('Failed to refresh transmissions');
    }
  }, [refetch]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent" />
        <span className="ml-2 text-sm text-gray-600">
          Loading transmissions...
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          Failed to load fax transmissions. Please try again.
        </AlertDescription>
      </Alert>
    );
  }

  if (transmissions.length === 0) {
    return (
      <div className="border rounded-lg">
        <div className="text-center py-12">
          <div className="text-sm text-gray-500 mb-2">
            No fax transmissions found
          </div>
          <div className="text-xs text-gray-400">
            No fax transmissions have been sent for this matter yet.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600">
          {transmissionData?.count || transmissions.length} transmission
          {transmissions.length !== 1 ? 's' : ''} for this matter
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefreshAll}
          className="flex items-center gap-2"
        >
          <ArrowPathIcon className="h-4 w-4" />
          Refresh All
        </Button>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Recipient</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Sent</TableHead>
              <TableHead>Delivered</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {transmissions.map((transmission: FaxTransmission) => (
              <TableRow key={transmission.id}>
                <TableCell className="font-medium">
                  {formatPhoneNumber(transmission.recipient_number)}
                </TableCell>
                <TableCell>
                  <Badge variant={getStatusBadgeVariant(transmission.status)}>
                    {transmission.status}
                  </Badge>
                </TableCell>
                <TableCell>
                  {format(
                    new Date(transmission.created_at),
                    'MMM d, yyyy h:mm a',
                  )}
                </TableCell>
                <TableCell>
                  {transmission.sent_at
                    ? format(
                        new Date(transmission.sent_at),
                        'MMM d, yyyy h:mm a',
                      )
                    : '-'}
                </TableCell>
                <TableCell>
                  {transmission.delivered_at
                    ? format(
                        new Date(transmission.delivered_at),
                        'MMM d, yyyy h:mm a',
                      )
                    : '-'}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => downloadFax(transmission)}
                              disabled={
                                !isDownloadAvailable(transmission) ||
                                isDownloading
                              }
                              className="h-8 w-8"
                            >
                              <ArrowDownTrayIcon className="h-4 w-4" />
                            </Button>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>
                            {isDownloadAvailable(transmission)
                              ? 'Download fax'
                              : getDownloadDisabledReason(transmission)}
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>

                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleRefreshStatus(transmission.id)}
                            disabled={refreshingId === transmission.id}
                            className="h-8 w-8"
                          >
                            <ArrowPathIcon
                              className={`h-4 w-4 ${
                                refreshingId === transmission.id
                                  ? 'animate-spin'
                                  : ''
                              }`}
                            />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Refresh status</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
